import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Pill, RefreshCw, Trash2, PawPrint, AlertTriangle, MessageCircle, CalendarClock, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";

const fmtDate = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
  catch { return iso; }
};

const STATUS_BADGE = {
  overdue: { label: "Atrasado", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  due:     { label: "Vencendo", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  scheduled: { label: "No prazo", cls: "bg-muted text-muted-foreground border-border" },
  renewed: { label: "Renovado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Cancelado", cls: "bg-muted text-muted-foreground border-border" },
};

const buildWhatsAppText = (m) => {
  const first = (m.client_name || "").split(" ")[0] || "tutor(a)";
  const pet = m.pet_name;
  const prod = m.product_name;
  const overdue = m.status === "overdue";
  const due = m.status === "due";
  if (overdue) {
    return (
      `Oi ${first}! Passei aqui pra lembrar que o(a) ${pet} precisa aplicar novamente o ${prod} — a última aplicação foi em ${fmtDate(m.applied_at)} e a próxima seria em ${fmtDate(m.next_due_at)}. Quer marcar de vir buscar hoje? 🐾`
    );
  }
  if (due) {
    return (
      `Oi ${first}! Passando pra avisar que o(a) ${pet} vai precisar aplicar o ${prod} em ${fmtDate(m.next_due_at)}. Que tal já garantir? 😊`
    );
  }
  return `Oi ${first}! Lembrete: o(a) ${pet} vai precisar do ${prod} em ${fmtDate(m.next_due_at)}.`;
};

export default function TenantPetMedications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, due: 0, scheduled: 0 });
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [renewing, setRenewing] = useState(null);
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/pet-medications");
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/tenant/clients?limit=500").then(({ data }) => setClients(data.items || [])).catch(() => {});
    api.get("/tenant/inventory/products").then(({ data }) => setProducts(data.items || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((m) =>
      (m.pet_name || "").toLowerCase().includes(s)
      || (m.client_name || "").toLowerCase().includes(s)
      || (m.product_name || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const remove = async (id) => {
    if (!window.confirm("Cancelar esse ciclo? O sistema não vai mais lembrar dele.")) return;
    try { await api.delete(`/tenant/pet-medications/${id}`); toast.success("Ciclo cancelado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const notifyTutor = (m) => {
    sendWhatsApp({
      phone: m.client_phone,
      text: buildWhatsAppText(m),
      clientId: m.client_id,
      context: "pet_medication_return",
    });
  };

  return (
    <div className="space-y-5" data-testid="pet-medications-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Pill className="w-5 h-5 text-tenant" /> Medicações dos Pets
          </h1>
          <p className="text-sm text-muted-foreground">Registre aplicações/vendas de medicamentos e o sistema avisa quando o pet precisa voltar.</p>
        </div>
        <Button onClick={() => setOpenNew(true)} className="gap-2 tenant-btn" data-testid="pet-med-new-btn">
          <Plus className="w-4 h-4" /> Registrar aplicação
        </Button>
      </div>

      {/* Contadores */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 rounded-tenant border-border" data-testid="pet-med-count-overdue">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Atrasados</div>
          <div className="text-2xl font-display font-semibold text-destructive mt-1">{counts.overdue || 0}</div>
        </Card>
        <Card className="p-4 rounded-tenant border-border" data-testid="pet-med-count-due">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Próximos 7d</div>
          <div className="text-2xl font-display font-semibold text-amber-600 mt-1">{counts.due || 0}</div>
        </Card>
        <Card className="p-4 rounded-tenant border-border" data-testid="pet-med-count-scheduled">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">No prazo</div>
          <div className="text-2xl font-display font-semibold mt-1">{counts.scheduled || 0}</div>
        </Card>
        <Card className="p-4 rounded-tenant border-border">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Total ativo</div>
          <div className="text-2xl font-display font-semibold mt-1">{(counts.overdue || 0) + (counts.due || 0) + (counts.scheduled || 0)}</div>
        </Card>
      </div>

      {/* Busca */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input placeholder="Buscar por pet, tutor ou produto…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="pet-med-search" />
      </div>

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">
          {items.length === 0
            ? <>Nenhuma aplicação registrada ainda. Clique em <strong>Registrar aplicação</strong> para começar.</>
            : "Nenhum resultado para essa busca."}
        </Card>
      ) : (
        <Card className="rounded-tenant border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Pet / Tutor</th>
                  <th className="text-left px-4 py-3">Produto</th>
                  <th className="text-left px-4 py-3">Aplicado em</th>
                  <th className="text-left px-4 py-3">Próxima</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const badge = STATUS_BADGE[m.status] || STATUS_BADGE.scheduled;
                  return (
                    <tr key={m.id} className="border-t border-border" data-testid={`pet-med-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 font-medium"><PawPrint className="w-3.5 h-3.5 text-tenant" /> {m.pet_name}</div>
                        <div className="text-xs text-muted-foreground">{m.client_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{m.product_name}</div>
                        <div className="text-xs text-muted-foreground">Qtd: {m.quantity} · ciclo {m.interval_days}d</div>
                      </td>
                      <td className="px-4 py-3 text-sm">{fmtDate(m.applied_at)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 text-muted-foreground" /> {fmtDate(m.next_due_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${badge.cls}`}>
                          {m.status === "overdue" && <AlertTriangle className="w-3 h-3" />}
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {(m.status === "due" || m.status === "overdue") && m.client_phone && (
                            <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => notifyTutor(m)} data-testid={`pet-med-whats-${m.id}`}>
                              <MessageCircle className="w-3.5 h-3.5" /> Avisar tutor
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => setRenewing(m)} data-testid={`pet-med-renew-${m.id}`}>
                            <RefreshCw className="w-3.5 h-3.5 text-emerald-600" /> Aplicou de novo
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(m.id)} data-testid={`pet-med-delete-${m.id}`} title="Cancelar ciclo">
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {openNew && <ApplyDialog open={openNew} onClose={() => setOpenNew(false)} onSaved={load} clients={clients} products={products} />}
      {renewing && <RenewDialog open={!!renewing} record={renewing} onClose={() => setRenewing(null)} onSaved={() => { setRenewing(null); load(); }} />}
    </div>
  );
}

function ApplyDialog({ open, onClose, onSaved, clients, products }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    client_id: "", pet_name: "", product_id: "", applied_at: today,
    interval_days: 30, quantity: 1, decrement_stock: true, notes: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.client_id) return toast.error("Selecione o tutor");
    if (!form.pet_name.trim()) return toast.error("Informe o nome do pet");
    if (!form.product_id) return toast.error("Selecione o produto/medicamento do estoque");
    setBusy(true);
    try {
      await api.post("/tenant/pet-medications", {
        ...form,
        interval_days: Number(form.interval_days),
        quantity: Number(form.quantity),
      });
      toast.success("Aplicação registrada. Sistema vai lembrar você antes do vencimento.");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="pet-med-apply-dialog">
        <DialogHeader><DialogTitle>Registrar aplicação de medicamento</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tutor</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="pet-med-client"><SelectValue placeholder="Escolher tutor…" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nome do pet</Label>
              <Input value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} data-testid="pet-med-petname" placeholder="Ex: Bob" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Medicamento/Produto</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
              <SelectTrigger data-testid="pet-med-product"><SelectValue placeholder="Escolher do estoque…" /></SelectTrigger>
              <SelectContent>
                {products.length === 0
                  ? <SelectItem value="_none" disabled>Cadastre produtos no estoque primeiro</SelectItem>
                  : products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.stock_quantity != null ? ` · ${p.stock_quantity} em estoque` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">É obrigatório vincular a um produto do estoque para controle de venda.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Data aplicação</Label>
              <Input type="date" value={form.applied_at} onChange={(e) => setForm({ ...form, applied_at: e.target.value })} data-testid="pet-med-applied" />
            </div>
            <div className="space-y-1.5">
              <Label>Próxima em</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={730} value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} data-testid="pet-med-interval" />
                <span className="text-xs text-muted-foreground shrink-0">dias</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Quantidade</Label>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="pet-med-qty" />
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <input id="dec-stock" type="checkbox" className="w-4 h-4" checked={form.decrement_stock} onChange={(e) => setForm({ ...form, decrement_stock: e.target.checked })} data-testid="pet-med-decstock" />
            <label htmlFor="dec-stock" className="cursor-pointer">Dar baixa no estoque agora</label>
          </div>
          <div className="space-y-1.5">
            <Label>Observações <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pet-med-notes" placeholder="Ex: aplicado no ombro esquerdo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="tenant-btn gap-2" data-testid="pet-med-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenewDialog({ open, record, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    applied_at: today,
    interval_days: record.interval_days || 30,
    quantity: 1,
    decrement_stock: true,
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/tenant/pet-medications/${record.id}/renew`, {
        ...form,
        interval_days: Number(form.interval_days),
        quantity: Number(form.quantity),
      });
      toast.success("Renovado! Próximo lembrete agendado.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="pet-med-renew-dialog">
        <DialogHeader><DialogTitle>Aplicou de novo — {record.pet_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2 text-sm">
          <div className="text-muted-foreground">
            Registrando nova aplicação de <strong>{record.product_name}</strong> para <strong>{record.pet_name}</strong> (tutor: {record.client_name}).
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Aplicado em</Label>
              <Input type="date" value={form.applied_at} onChange={(e) => setForm({ ...form, applied_at: e.target.value })} data-testid="pet-med-renew-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Próxima em (dias)</Label>
              <Input type="number" min={1} max={730} value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} data-testid="pet-med-renew-interval" />
            </div>
            <div className="space-y-1.5">
              <Label>Quantidade</Label>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="pet-med-renew-qty" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input id="dec-stock2" type="checkbox" className="w-4 h-4" checked={form.decrement_stock} onChange={(e) => setForm({ ...form, decrement_stock: e.target.checked })} data-testid="pet-med-renew-decstock" />
            <label htmlFor="dec-stock2" className="cursor-pointer">Dar baixa no estoque agora</label>
          </div>
          <div className="space-y-1.5">
            <Label>Observações <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pet-med-renew-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="tenant-btn gap-2" data-testid="pet-med-renew-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Registrar renovação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
